import { useEffect, useMemo, useState } from 'react';
import { resolveMode } from '@billing/branding';
import type { ResolvedMode, ThemeMode } from '@billing/branding';

/**
 * The mode actually painted after `SYSTEM` is resolved (`LIGHT` / `DARK`).
 * Re-exported from the branding SDK so consumers share one spelling.
 */
export type { ResolvedMode as ResolvedThemeMode } from '@billing/branding';

/** The media query backing `SYSTEM` — the reader's device preference. */
const DARK_QUERY = '(prefers-color-scheme: dark)';

/*
 * Live `prefers-color-scheme: dark`.
 *
 * The SDK's `resolveMode` reads the media query at call time and does not
 * subscribe, so a `SYSTEM` theme would freeze at whatever the device reported on
 * first render. A shopper whose device flips to dark (or hits a scheduled night
 * shift) with the payment page open would keep the light palette until reload.
 * This hook provides the subscription; `useResolvedThemeMode` re-resolves on
 * every change (PAA-3012).
 *
 * Reads the current value synchronously in the initializer, so the first paint is
 * already correct rather than briefly light. Mirrors the subscription pattern in
 * `hooks/useMediaQuery`.
 *
 * The subscription is only wired up when `enabled` — an explicit LIGHT/DARK theme
 * does not follow the device, so listening for it would re-render every themed
 * subtree on an OS flip for an answer that cannot change.
 *
 * @param enabled Whether the caller's mode actually depends on the device.
 * @return Whether the reader's device currently prefers a dark colour scheme.
 */
const usePrefersDark = (enabled: boolean): boolean => {
  const [prefersDark, setPrefersDark] = useState(() => window.matchMedia(DARK_QUERY).matches);

  useEffect(() => {
    if (!enabled) return undefined;
    const mql = window.matchMedia(DARK_QUERY);
    const onChange = (): void => setPrefersDark(mql.matches);
    if (mql.addEventListener) {
      mql.addEventListener('change', onChange);
    } else {
      // Safari < 14 / legacy Edge.
      mql.addListener(onChange);
    }
    return () => {
      if (mql.removeEventListener) {
        mql.removeEventListener('change', onChange);
      } else {
        mql.removeListener(onChange);
      }
    };
  }, [enabled]);

  return prefersDark;
};

/*
 * Resolve a canonical theme mode to the mode actually painted, staying live.
 *
 * `SYSTEM` follows the reader's device and re-resolves when it changes; LIGHT and
 * DARK are returned as-is and never re-resolve. `prefersDark` is passed through
 * the dependency list purely to trigger the re-resolve — `resolveMode` reads
 * `window.matchMedia` itself, so the hook must re-run it rather than invert it
 * here (that would duplicate the SDK's fallback rules).
 *
 * @param mode The canonical theme mode, if one is configured.
 * @return The mode actually painted.
 */
export const useResolvedThemeMode = (mode?: ThemeMode): ResolvedMode => {
  const prefersDark = usePrefersDark(mode === 'SYSTEM');
  // `prefersDark` is a dependency because it is what invalidates a SYSTEM theme:
  // re-running `resolveMode` re-reads the media query, which is the SDK's own
  // source of truth for SYSTEM. Inverting `prefersDark` here instead would
  // duplicate the SDK's fallback rules (including its LIGHT fallback when the
  // query cannot be read).
  return useMemo(() => resolveMode(mode ?? 'LIGHT'), [mode, prefersDark]);
};
